// hooks/useAlertAIHandler.ts
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getAlertConfigsSelect } from "../features/admin/alertValues/api/alertValuesApi";
import { AlertStatusEnum } from "../features/alerts/types";
import { useAlert } from "./useAlert";

interface TopicWithAlert {
    alertAI?: string | number | null;
}

interface AlertAIContext {
    alertAI?: number | null;
    topics: TopicWithAlert[];
    hasAlert?: boolean;
    alertStatus?: AlertStatusEnum | null;
    alertId?: number | null;
}

interface UseAlertAIHandlerParams<T extends AlertAIContext> {
    state: T;
    setAlertAI: (value: number | null) => void;
    setTopicAlertAI: (index: number, value: string) => void;
    onDeleteAlertConfirmed: () => void;
    fromLocation?: boolean;
}

export function useAlertAIHandler<T extends AlertAIContext>({
    state,
    setAlertAI,
    setTopicAlertAI,
    onDeleteAlertConfirmed,
    fromLocation = false,
}: UseAlertAIHandlerParams<T>) {
    const { t } = useTranslation();
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation });

    const [showModalDeleteAlert, setShowModalDeleteAlert] = useState(false);
    const [prevAlertValue, setPrevAlertValue] = useState<number | null>(null);
    const [prevIndexAlertValue, setPrevIndexAlertValue] = useState<number | null>(null);

    const { data: alertConfigs = [] } = useQuery({
        queryKey: ["alertConfigs"],
        queryFn: getAlertConfigsSelect,
    });

    const generatesAlertForValue = (value?: number | null) =>
        alertConfigs.find(c => c.value === value)?.generatesAlert === true;

    const hasTopicAlertAI = () =>
        state.topics?.some(tp => generatesAlertForValue(Number(tp.alertAI))) ?? false;

    const restorePrevValue = () => {
        if (prevIndexAlertValue === null) {
            setAlertAI(prevAlertValue);
        } else {
            setTopicAlertAI(prevIndexAlertValue, String(prevAlertValue));
        }
    };

    const handleDeleteAlert = (deleteAlert: boolean) => {
        setShowModalDeleteAlert(false);
        if (deleteAlert) {
            onDeleteAlertConfirmed();
        } else {
            restorePrevValue();
        }
    };

    const handleAlertAI = (newValue: number | null) => {
        const prevAlertAI = state.alertAI ?? null;
        setAlertAI(newValue);

        const generateAlert = generatesAlertForValue(newValue);
        if (!generateAlert) {
            if (!hasTopicAlertAI() && state.hasAlert) {
                if (state.alertStatus !== AlertStatusEnum.PendingToRead) {
                    showAlert({ message: t("alert:states.alertAlreadyAssigned"), type: "warning", duration: 8000 });
                    setAlertAI(prevAlertAI);
                } else {
                    setShowModalDeleteAlert(true);
                    setPrevIndexAlertValue(null);
                    setPrevAlertValue(prevAlertAI);
                }
            }
        } else if (state.hasAlert) {
            showAlert({ message: t("common:alerts.alarmAlreadyGenerated"), type: "info", duration: 8000 });
        }
    };

    const handleTopicAlertAI = (newValue: number | null, index: number) => {
        if (generatesAlertForValue(Number(newValue)) && state.hasAlert) {
            showAlert({ message: t("common:alerts.alarmAlreadyGenerated"), type: "info", duration: 8000 });
            return;
        }

        const generateAlert = generatesAlertForValue(state.alertAI);
        const prevAlert = state.topics[index].alertAI;
        const nextStr = newValue == null ? "" : String(newValue);
        setTopicAlertAI(index, nextStr);

        const wouldHaveAlert = state.topics.some((tp, i) =>
            generatesAlertForValue(Number(i === index ? nextStr : tp.alertAI))
        );

        if (!generateAlert && !wouldHaveAlert && state.hasAlert) {
            if (state.alertStatus !== AlertStatusEnum.PendingToRead) {
                showAlert({ message: t("common:alerts.alertHasBeenDeleted"), type: "warning", duration: 8000 });
                setTopicAlertAI(index, prevAlert == null ? "" : String(prevAlert));
            } else {
                setShowModalDeleteAlert(true);
                setPrevIndexAlertValue(index);
                setPrevAlertValue(Number(prevAlert));
            }
        }
    };

    return {
        handleAlertAI,
        handleTopicAlertAI,
        handleDeleteAlert,
        showModalDeleteAlert,
        alert,
        hideAlert,
        showAlert,
        isVisible,
    };
}